import {
    ELEMENT_UPDATE,
    ELEMENT_UPDATE_TYPE,
    ELEMENT_MOVE_MULTI,
    ELEMENT_MOVE_OPERATIONS,
    ELEMENT_CREATE,
    ELEMENT_DELETE,
    ELEMENT_DIFF_UPDATE,
} from './elementConstants';
import { ELEMENTS_LOAD } from './elementsConstants';
import { UnknownAction } from 'redux';
import { ActionMove, ActionOrigin } from '../actions/actionTypes';
import { MNElementContent, MNElementLocation, MNElementMeta } from './elementModelTypes';
import { ElementType } from './elementTypes';

// A single element update entry as used by updateMultipleElements / updateElement
export interface ElementUpdateEntry {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changes: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    meta?: Record<string, any>;
}

export type ElementUpdateType = (typeof ELEMENT_UPDATE_TYPE)[keyof typeof ELEMENT_UPDATE_TYPE];

// The Redux action dispatched by updateMultipleElements (and indirectly by updateElement)
export interface ElementUpdateAction extends UnknownAction {
    type: typeof ELEMENT_UPDATE;
    // Optional fine-grained categorisation of the update (e.g. REACTION, ASSIGNMENT, etc.)
    updateType?: ElementUpdateType;
    // The set of element modifications to apply
    updates: ElementUpdateEntry[];
    // The inverse operations used to support undo/redo
    undoUpdates?: ElementUpdateEntry[];
    // Additional actions to dispatch when performing an UNDO of this action
    batchUndoActions?: UnknownAction[];
    // Additional actions to dispatch when performing a REDO of this action
    batchRedoActions?: UnknownAction[];
    // Whether the meta.modifiedTime for the element should be updated
    silent?: boolean;
    // Whether this action should be synchronised with the server
    sync: boolean;
    // Millisecond timestamp captured when the action was created
    timestamp: number;
    // Correlates related actions (e.g. for undo/redo purposes)
    transactionId?: number;
    // Allows the source of the action to be inspected, e.g. to prevent infinite loops in middleware
    //  This will usually be undefined, and is only used when specific origin handling is required
    actionOrigin?: ActionOrigin;
}

// Convenience type guard (helps when narrowing unknown Redux actions)
export const isActionElementUpdate = (action: UnknownAction): action is ElementUpdateAction =>
    action.type === ELEMENT_UPDATE && Array.isArray((action as ElementUpdateAction).updates);

// ----------------------------------------------
// ElementMoveMultiAction types (moveMultipleElements)
// ----------------------------------------------

// All possible move operation identifiers
export type ElementMoveOperation = (typeof ELEMENT_MOVE_OPERATIONS)[keyof typeof ELEMENT_MOVE_OPERATIONS];

// The action dispatched by moveMultipleElements / moveElement
export interface ElementMoveMultiAction extends UnknownAction {
    type: typeof ELEMENT_MOVE_MULTI;
    moves: ActionMove[];
    // Optional payload containing measurements of moved elements used for canvas sizing.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialMeasurements?: Record<string, any>;
    // Whether this action should be synchronised with the server
    sync: boolean;
    // Millisecond timestamp captured when the action was created
    timestamp: number;
    // Correlates related actions (e.g. for undo/redo purposes)
    transactionId?: number;
    // Identifies the semantic category of the move (DROP, COLLISION, TRASH, etc.)
    moveOperation?: ElementMoveOperation;
    // Monitoring/analytics metadata attached downstream in middleware
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    monitoring?: { operation?: ElementMoveOperation; [key: string]: any };
}

export const isActionElementMoveMulti = (action: UnknownAction): action is ElementMoveMultiAction =>
    action.type === ELEMENT_MOVE_MULTI && Array.isArray((action as ElementMoveMultiAction).moves);

export interface ElementCreateAction extends UnknownAction {
    type: typeof ELEMENT_CREATE;
    id: string;
    elementType: ElementType;
    location: MNElementLocation;
    content: MNElementContent;
    meta: MNElementMeta;
    acl?: { [access: string]: number };
    sync: boolean;
    timestamp: number;
    transactionId?: number;
    duplicate?: boolean;
    duplicatedId?: string;
    creationSource?: string;
    markAsFetched?: boolean;
}

export interface ElementDeleteAction extends UnknownAction {
    type: typeof ELEMENT_DELETE;
    id: string;
    elementType?: ElementType;
    location?: Partial<MNElementLocation>;
    sync?: boolean;
}

export interface ElementDiffUpdateAction extends UnknownAction {
    type: typeof ELEMENT_DIFF_UPDATE;
    id: string;
    changes: Partial<MNElementContent>;
    silent?: boolean;
    sync?: boolean;
    timestamp?: number;
    transactionId?: number;
}

export interface ElementsLoadAction extends UnknownAction {
    type: typeof ELEMENTS_LOAD;
    elements: Record<string, Partial<{ id: string; _id: string }>>;
    deletedElementIds?: string[];
}
